import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { createNote } from "@/lib/composerNotes";

type Props = {
  workspaceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
};

export const NoteQuickAddSheet = ({ workspaceId, open, onOpenChange, onSaved }: Props) => {
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle("");
      setBody("");
      setSaving(false);
    }
  }, [open]);

  const handleSave = async () => {
    if (!workspaceId) {
      toast({ title: "No active workspace", variant: "destructive" });
      return;
    }
    if (!title.trim() && !body.trim()) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      await createNote({ workspaceId, title, body });
      toast({ title: "Note saved" });
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast({ title: "Couldn't save note", description: (err as Error).message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-3 sm:max-w-xl">
        <SheetHeader className="space-y-1">
          <SheetTitle>New note</SheetTitle>
          <SheetDescription className="text-xs">
            Capture a quick thought. Saves into the Notes root folder.
          </SheetDescription>
        </SheetHeader>
        <Input
          placeholder="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          spellCheck
          autoComplete="off"
        />
        <Textarea
          placeholder="Start typing…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="min-h-[50vh] flex-1 resize-none leading-relaxed"
          spellCheck
          autoComplete="off"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              void handleSave();
            }
          }}
        />
        <div className="flex items-center justify-between">
          <span className="text-[11px] text-muted-foreground">Cmd/Ctrl + Enter to save</span>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "Save note"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
